import type { ChangeEvent } from 'react';
import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { RadioButton } from 'src/components/atoms/RadioButton/RadioButton';
import * as styles from './styles';

type RadioButtonData = {
  checked: boolean;
  value: string;
  labelText: string;
  secondaryLabelText?: string;
};

export interface RadioButtonGroupProps {
  accessibleLabel: string;
  accessibleLabelElementType?: keyof JSX.IntrinsicElements;
  accessibleLabelElementId?: string;
  name: string;
  hideLabel?: boolean;
  onChange: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  radioButtonData: RadioButtonData[];
}

/**
 * The accessibleLabelElementType prop should be a string which represents the type of HTML element to use for the label e.g. 'h1'.
 * The accessibleLabelElementId prop should contain a string to customise the id attribute for the label element containing the accessible label.
 */
export const RadioButtonGroup = ({
  name,
  accessibleLabel,
  accessibleLabelElementType: AccessibleLabelElementTag = 'p',
  accessibleLabelElementId = `${name}-label`,
  hideLabel = false,
  onChange,
  radioButtonData,
}: RadioButtonGroupProps) => (
  <div role="group" aria-labelledby={accessibleLabelElementId}>
    <span id={accessibleLabelElementId} css={hideLabel ? visuallyHidden : undefined}>
      <AccessibleLabelElementTag>{accessibleLabel}</AccessibleLabelElementTag>
    </span>
    {radioButtonData.map(({ checked, labelText, value: radioButtonValue, secondaryLabelText }) => (
      <div key={radioButtonValue} css={styles.radioWrapper} data-testid={radioButtonValue}>
        <RadioButton
          name={name}
          labelText={labelText}
          value={radioButtonValue}
          checked={checked}
          onChange={(e) => onChange(radioButtonValue, e)}
        />
        {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
      </div>
    ))}
  </div>
);
